import React from 'react';
import {
    Box,
    Container,
    Typography,
    Grid,
    Button,
    Stack,
    Divider,
    alpha
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import BusinessIcon from '@mui/icons-material/Business';
import GroupsIcon from '@mui/icons-material/Groups';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SectionWrapper from '../components/SectionWrapper';

const ClientsPage = () => {
    const navigate = useNavigate();
    const brandRed = '#CC0000';
    const offBlack = '#0a1128';

    const clients = [
        'Banco do Brasil',
        'Caixa',
        'Petrobras',
        'Vale',
        'Governo Federal',
        'Eletrobras',
        'Ministério da Saúde',
        'CNJ',
        'TCU',
        'STF'
    ];

    const impacts = [
        { number: '+50', label: 'Projetos de Grande Porte', icon: <BusinessIcon /> },
        { number: '10Mi+', label: 'Usuários Impactados', icon: <GroupsIcon /> },
        { number: '100%', label: 'Compromisso com Entrega', icon: <EmojiEventsIcon /> }
    ];

    return (
        <SectionWrapper
            id="clients-page"
            sx={{ bgcolor: '#ffffff' }}
        >

            {/* 1. HERO SECTION */}
            <Box sx={{ bgcolor: offBlack, pt: { xs: 16, md: 24 }, pb: { xs: 12, md: 20 }, color: 'white', position: 'relative', overflow: 'hidden' }}>
                {/* Abstract Background Element */}
                <Box sx={{
                    position: 'absolute', top: '-10%', right: '-5%', width: '800px', height: '800px',
                    background: `linear-gradient(135deg, ${alpha(brandRed, 0.1)} 0%, transparent 60%)`,
                    borderRadius: '50%', pointerEvents: 'none', filter: 'blur(80px)'
                }} />

                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
                    <Stack spacing={4} maxWidth="800px">
                        <Typography variant="overline" sx={{ color: brandRed, fontWeight: 700, letterSpacing: '0.2em' }}>
                            PORTFÓLIO DE IMPACTO
                        </Typography>
                        <Typography variant="h1" sx={{ fontSize: { xs: '3rem', md: '5rem' }, fontWeight: 800, lineHeight: 1.1, letterSpacing: '-0.02em' }}>
                            Grandes missões exigem grandes parceiros.
                        </Typography>
                        <Typography variant="h5" sx={{ color: 'grey.400', fontWeight: 300, lineHeight: 1.6, maxWidth: '650px' }}>
                            Temos orgulho de ser a base tecnológica para as instituições que movem o Brasil. De bancos federais a órgãos estratégicos, a R5 entrega robustez onde falhar não é uma opção.
                        </Typography>
                    </Stack>
                </Container>
            </Box>

            {/* 2. IMPACT NUMBERS */}
            <Box sx={{ bgcolor: brandRed, color: 'white', py: 8 }}>
                <Container maxWidth="lg">
                    <Grid container spacing={4} justifyContent="center" alignItems="center">
                        {impacts.map((item, idx) => (
                            <Grid item xs={12} md={4} key={idx}>
                                <Stack direction="row" alignItems="center" spacing={3} justifyContent={{ xs: 'center', md: 'center' }}>
                                    <Box sx={{ bgcolor: 'rgba(255,255,255,0.2)', p: 1.5, borderRadius: '50%', display: 'flex' }}>
                                        {React.cloneElement(item.icon, { sx: { fontSize: 32 } })}
                                    </Box>
                                    <Box>
                                        <Typography variant="h3" sx={{ fontWeight: 800, lineHeight: 1 }}>
                                            {item.number}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500, opacity: 0.9, textTransform: 'uppercase', letterSpacing: '0.05em', mt: 0.5 }}>
                                            {item.label}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* 3. CLIENT WALLL */}
            <Container maxWidth="lg" sx={{ py: 16 }}>
                <Box sx={{ textAlign: 'center', mb: 10 }}>
                    <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.2em' }}>
                        QUEM CONFIA NA R5
                    </Typography>
                    <Typography variant="h2" sx={{ mt: 2, fontWeight: 800, color: offBlack }}>
                        Nossos Clientes
                    </Typography>
                </Box>

                <Grid container spacing={3}>
                    {clients.map((client, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                            <Box
                                sx={{
                                    height: '180px',
                                    bgcolor: '#fafafa',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: '4px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    '&:hover': {
                                        transform: 'translateY(-8px)',
                                        boxShadow: '0 20px 40px rgba(0,0,0,0.06)',
                                        borderColor: brandRed,
                                        bgcolor: 'white',
                                        '& .client-text': {
                                            color: brandRed,
                                            transform: 'scale(1.1)'
                                        },
                                        '& .corner-accent': {
                                            transform: 'translate(0, 0)'
                                        }
                                    }
                                }}
                            >
                                {/* Decorative Accent on Hover */}
                                <Box className="corner-accent" sx={{
                                    position: 'absolute', top: 0, right: 0, width: 40, height: 40,
                                    background: `linear-gradient(to bottom left, ${brandRed} 50%, transparent 50%)`,
                                    transform: 'translate(100%, -100%)',
                                    transition: 'transform 0.3s ease-out'
                                }} />

                                <Typography
                                    className="client-text"
                                    variant="h5"
                                    sx={{
                                        fontWeight: 700,
                                        color: 'text.secondary',
                                        textTransform: 'uppercase',
                                        textAlign: 'center',
                                        px: 3,
                                        letterSpacing: '-0.02em',
                                        transition: 'all 0.4s ease'
                                    }}
                                >
                                    {client}
                                </Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            <Divider />

            {/* 4. CTA FOOTER */}
            <Box sx={{ py: 12, bgcolor: '#FAFAFA', textAlign: 'center' }}>
                <Container maxWidth="md">
                    <Typography variant="h3" sx={{ fontWeight: 800, color: offBlack, mb: 3 }}>
                        Sua empresa no próximo nível.
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 400, mb: 5, maxWidth: '600px', mx: 'auto' }}>
                        Junte-se ao grupo de organizações que estão definindo o futuro da tecnologia na área pública e privada.
                    </Typography>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={() => navigate('/fale-conosco')}
                        sx={{
                            bgcolor: brandRed,
                            color: 'white',
                            px: 6,
                            py: 2,
                            borderRadius: '14px',
                            fontSize: '1.1rem',
                            fontWeight: 700,
                            boxShadow: '0 10px 30px rgba(204, 0, 0, 0.3)',
                            '&:hover': { bgcolor: '#CC0000', transform: 'translateY(-2px)' }
                        }}
                    >
                        Falar com Consultor
                    </Button>
                </Container>
            </Box>
        </SectionWrapper>
    );
};

export default ClientsPage;
